

import Vue from 'vue'
import Component from "vue-class-component";
import {namespace} from "vuex-class";
import {IShortLocker, IShortUser} from "@/model/shortInterfaces";
import UserSearch from "@/components/user/widget/UserSearch.vue";
import Datim from "@/components/shared/Datim.vue";
import LockerChip from "@/components/shared/chips/LockerChip.vue";
import CommunityChip from "@/components/shared/chips/CommunityChip.vue";
import UserListSummary from "@/components/user/UserListSummary.vue";
import UserIntegrationStatus from "@/components/user/UserIntegrationStatus.vue";
import {BoListPanel} from "bonomo/dist";
import PaginationBar from "@/components/shared/PaginationBar.vue";
import {BetterDataTableHeader} from "@/model/betterVuetifyInterfaces";

const lockerModule = namespace("lockerModule");
const userModule = namespace("userModule");

interface IUserListRow extends IShortUser {
  name: string;
  communityId?: number|null;
  communityName?: string|null;
  lockerName?: string|null;
}

@Component({
  components: {
    BoListPanel,
    CommunityChip,
    UserListSummary,
    UserIntegrationStatus,
    LockerChip,
    UserSearch,
    Datim,
    PaginationBar,
  }
})

export default class UserListView extends Vue {
  @userModule.Getter('userList')
  private users!: IShortUser[];

  @lockerModule.Getter('lockers')
  private lockers!: IShortLocker[];

  @userModule.Getter('currentPage')
  private currentPage!: number;

  @userModule.Getter('lastPage')
  private lastPage!: number;

  @userModule.Getter('total')
  private total!: number;

  @userModule.Getter('perPage')
  private perPage!: number;

  @userModule.Action('clearUsers')
  protected clearUsers!: () => Promise<any>;
  @userModule.Action('fetchUsers')
  protected fetchUsers!: (filters: any) => Promise<any>;

  protected get userRows(): IUserListRow[] {
    const lockersById: Record<number, IShortLocker> = {};
    this.lockers.forEach(locker => { lockersById[locker.id] = locker; });

    return this.users.map(user => {
      const locker = user.locker_id ? lockersById[user.locker_id] : undefined;
      // A user's own community takes precedence over their locker's community.
      const communityId = user.community_id || (locker && locker.community_id);
      const communityName = user.community_name
        || (locker && locker.community_id === communityId ? locker.community : null);

      return {
        ...user,
        name: [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
          || user.email || user.phone_number || '—',
        communityId,
        communityName,
        lockerName: user.locker_name || (locker && locker.name),
      };
    });
  }

  protected get headers(): BetterDataTableHeader[] {
    const compactColumn = {
      width: '1%',
      class: 'text-no-wrap px-2',
      cellClass: 'text-no-wrap px-2',
    };

    return [
      { text: this.$t('common.id'), align: 'start', sortable: false, value: 'id', ...compactColumn },
      { text: this.$t('common.name'), align: 'start', sortable: false, value: 'name' },
      { text: this.$t('communities.detail.title'), align: 'end', sortable: false, value: 'community', ...compactColumn },
      { text: this.$t('users.userList.integration'), align: 'start', sortable: false, value: 'is_from_integration', ...compactColumn },
      { text: this.$t('users.userList.createdAt'), align: 'start', sortable: false, value: 'created_at', ...compactColumn },
      { text: this.$t('users.user.fields.last_logged_in.title'), align: 'start', sortable: false, value: 'last_logged_in', ...compactColumn },
    ];
  }
  protected selectedPerPage: number = 15;
  protected readonly perPageOptions: number[] = [15, 30, 50, 100];

  protected loading: boolean = false;

  loadData(initial: boolean = false) {
    if (initial || !this.lockers.length) {
      this.$store.dispatch('lockerModule/fetchLockers');
    }
  }

  mounted() {
    this.loadData(true);
    this.fetchUsers({ page: 1, per_page: this.selectedPerPage });
  }

  toUserDetail(userId: number): any
  {
    return {name: 'user', params: { userId: `${ userId}`, }};
  }

  userRowClass(): string {
    return 'user-row';
  }

  gotoUser(user: IShortUser) {
    return this.$router.push({name: 'user', params: { userId: ''+user.id}});
  }

  protected onPageChange(page: number) {
    this.clearUsers().then(() => {
      this.fetchUsers({ page, per_page: this.selectedPerPage });
    });
  }

  protected onPerPageChange(value: number) {
    this.selectedPerPage = value;
    this.clearUsers().then(() => {
      this.fetchUsers({ page: 1, per_page: this.selectedPerPage });
    });
  }
}
